import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Card, CardHeader, Bar } from '@ui5/webcomponents-react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import httpRequest from "../../httpRequest";
import { CheckBox, Input, Switch } from '@ui5/webcomponents-react';
import { Label } from '@ui5/webcomponents-react';

const RolesCard = ({ onRolesChange }) => {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    can_add_invoice: false,
	can_add_expense: false,
    can_access_expense: false,
    can_general_scan: false,
    can_access_scans: false,
    can_access_all_scans: false,
    can_access_all_company_scans: false,
    can_user_management: false,
    can_settings: false,
    can_vendor: false,
    can_configurator: false,
	can_delete_invoice: false,
	can_delete_documents: false,
	can_add_documents: false,
	can_manage_documents: false,
	can_function_dashboard: false,
	can_company_dashboard: false,
	can_enterprise_dashboard: false
  });
  const [deleteRoleId, setDeleteRoleId] = useState(null);
   
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-roles`);
      // Filter out roles with id === 1
      const filteredRoles = response.data.roles.filter(role => role.id !== 1);
      setRoles(filteredRoles);
      setFilteredRoles(filteredRoles);
    } catch (error) {
      enqueueSnackbar("Failed to fetch roles", { variant: "error" });
    }
  };

 

  const handleOpenDialog = () => {
    setDialogTitle("Create Role");
    setFormData({
      name: "",
      can_add_invoice: false,
	  can_add_expense: false,
      can_access_expense: false,
      can_general_scan: false,
      can_access_scans: false,
      can_access_all_scans: false,
      can_access_all_company_scans: false,
      can_user_management: false,
      can_settings: false,
      can_vendor: false,
    can_configurator: false,
	  can_delete_invoice: false,
	  can_delete_documents: false,
	can_add_documents: false,
	can_manage_documents: false,
	can_function_dashboard: false,
	can_company_dashboard: false,
	can_enterprise_dashboard: false,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      // Prevent the dialog from closing when clicking on the backdrop
      return;
    }
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: checked
    }));
    if (name === "can_access_scans" && !checked) {
      setFormData(prevData => ({
        ...prevData,
        can_access_all_scans: false,
        can_access_all_company_scans: false
      }));
    }
  };

  const handleSwitchChange = (name) => {
    setFormData(prevData => {
      const newFormData = { ...prevData };

      if (name === "can_access_all_scans") {
        newFormData.can_access_all_scans = !prevData.can_access_all_scans;
        if (newFormData.can_access_all_scans) {
          newFormData.can_access_all_company_scans = true;
        }
      } else if (name === "can_access_all_company_scans") {
        newFormData.can_access_all_company_scans = !prevData.can_access_all_company_scans;
        if (!newFormData.can_access_all_company_scans) {
          newFormData.can_access_all_scans = false;
        }
      }

      // If both are toggled and company scans is turned off, turn off all scans as well
      if (newFormData.can_access_all_scans && !newFormData.can_access_all_company_scans) {
        newFormData.can_access_all_scans = false;
      }

      return newFormData;
    });
  };

  const handleNameChange = (e) => {
    const { value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      name: value
    }));
  };

  const handleSubmit = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/create-role`, formData);
      fetchRoles();
      setOpenDialog(false);
      enqueueSnackbar("Role created successfully", { variant: "success" });
      onRolesChange();
    } catch (error) {
      enqueueSnackbar("Failed to create role", { variant: "error" });
    }
  };

  const handleEdit = (role) => {
    setDialogTitle("Edit Role");
    setFormData({
      id: role.id,
      name: role.name,
      can_add_invoice: role.can_add_invoice,
	  can_add_expense: role.can_add_expense,
	  can_access_expense: role.can_access_expense,
      can_general_scan: role.can_general_scan,
      can_access_scans: role.can_access_scans,
      can_access_all_scans: role.can_access_all_scans,
      can_access_all_company_scans: role.can_access_all_company_scans,
      can_user_management: role.can_user_management,
      can_settings: role.can_settings,
      can_vendor: role.can_vendor,
    can_configurator: role.can_configurator,
	  can_delete_invoice: role.can_delete_invoice,
	  can_delete_documents: role.can_delete_documents,
      can_add_documents: role.can_add_documents,
      can_manage_documents: role.can_manage_documents,  
	  can_function_dashboard: role.can_function_dashboard,
	  can_company_dashboard: role.can_company_dashboard,
	  can_enterprise_dashboard: role.can_enterprise_dashboard,
    });
    setOpenDialog(true);
  };

  const handleEditSubmit = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/edit-role/${formData.id}`, formData);
      fetchRoles();
      setOpenDialog(false);
      enqueueSnackbar("Role updated successfully", { variant: "success" });
      onRolesChange();
    } catch (error) {
      enqueueSnackbar("Failed to update role", { variant: "error" });
    }
  };

  const handleOpenDeleteDialog = (roleId) => {
    setDeleteRoleId(roleId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (deleteRoleId) {
      try {
        await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-role/${deleteRoleId}`);
        fetchRoles();
        enqueueSnackbar("Role deleted successfully", { variant: "success" });
        onRolesChange();
      } catch (error) {
        enqueueSnackbar("Failed to delete role", { variant: "error" });
      }
      setOpenDeleteDialog(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    filterRoles(value);
  };

  const filterRoles = (searchTerm) => {
    if (!searchTerm) {
      setFilteredRoles(roles);
    } else {
      const filtered = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRoles(filtered);
    }
  };


  return (
    <>
      <Card header={<CardHeader subtitleText="Create and define new roles" titleText="Role Management" />} style={{ width: "100%", overflow: "hidden" }}>
        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ flex: "1", padding: "10px", marginRight: "20px" }}
          />
          <ButtonContained onClick={handleOpenDialog}><AddIcon /> Add New</ButtonContained>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto', minWidth: 600 }}>Name</TableCell>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRoles.map(role => (
              <TableRow key={role.id}>
                <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{role.name}</TableCell>
                <TableCell>
                  <ButtonContained style={{ marginRight: 10 }} onClick={() => handleEdit(role)}>
                    Edit
                  </ButtonContained>
                  <ButtonOutlined color="secondary" onClick={() => handleOpenDeleteDialog(role.id)}>
                    Delete
                  </ButtonOutlined>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
  <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>Confirm Delete</DialogTitle>
  <DialogContent>
        Are you sure you want to delete this role?
        </DialogContent>
        <DialogActions>
              <ButtonOutlined style={{ marginRight: 5 }} onClick={handleCloseDeleteDialog}>Cancel</ButtonOutlined>
              <ButtonContained onClick={handleDelete} color="error">Delete</ButtonContained>
            </DialogActions>
      </Dialog>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth  slotProps={{
        backdrop: {
          onClick: (e) => {
            e.stopPropagation(); // Prevent the click from propagating
          },
        },
      }}>
  <DialogTitle sx={{ fontFamily: "SapFiori72, sans-serif" }}>{dialogTitle}</DialogTitle>
  <DialogContent>
      
          
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
		
		
		  <Label>Name</Label>
          <Input placeholder="Name" value={formData.name} onInput={handleNameChange} />
		  
		  <Label>KPI Access Permissions</Label>
		<CheckBox text="Department-Level Insights" name="can_function_dashboard" checked={formData.can_function_dashboard} onChange={handleChange} />
		<CheckBox text="Company-Wide Insights" name="can_company_dashboard" checked={formData.can_company_dashboard} onChange={handleChange} />
		<CheckBox text="Enterprise-Level Insights" name="can_enterprise_dashboard" checked={formData.can_enterprise_dashboard} onChange={handleChange} />
		
		  
		    <Label>Invoice Permissions</Label>
          <CheckBox text="Can Add Invoice" name="can_add_invoice" checked={formData.can_add_invoice} onChange={handleChange} />
          <CheckBox text="Can Access Scans" name="can_access_scans" checked={formData.can_access_scans} onChange={handleChange} />

<div style={{ marginBottom: -10, marginTop: -10, marginLeft: '1rem', marginRight: '1rem' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Label style={{ marginRight: '10px' }}>All Scans</Label>
    <Switch
      checked={formData.can_access_all_scans}
      disabled={!formData.can_access_scans}
      onChange={() => handleSwitchChange("can_access_all_scans")}
    />
  </div>
</div>

<div style={{  marginLeft: '1rem', marginRight: '1rem' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Label style={{ marginRight: '10px' }}>All Functions Within Company(s)</Label>
    <Switch
      checked={formData.can_access_all_company_scans}
      disabled={!formData.can_access_scans}
      onChange={() => handleSwitchChange("can_access_all_company_scans")}
    />
  </div>
</div>
<CheckBox text="Can Delete Invoices" name="can_delete_invoice" checked={formData.can_delete_invoice} onChange={handleChange} />

		 
		 

<Label>Expense Permissions</Label>

          <CheckBox text="Can Add Expense" name="can_add_expense" checked={formData.can_add_expense} onChange={handleChange} />
			  {/*<CheckBox text="Can Access Expenses" name="can_access_expense" checked={formData.can_access_expense} onChange={handleChange} />*/}

<Label>Document Management</Label>
<CheckBox text="Can Upload Documents" name="can_add_documents" checked={formData.can_add_documents} onChange={handleChange} />
<CheckBox text="Can Manage Documents" name="can_manage_documents" checked={formData.can_manage_documents} onChange={handleChange} />
<CheckBox text="Can Delete Documents" name="can_delete_documents" checked={formData.can_delete_documents} onChange={handleChange} />

          <Label>General Permissions</Label>
          <CheckBox text="Can General Scan" name="can_general_scan" checked={formData.can_general_scan} onChange={handleChange} />

          <Label>System Permissions</Label>
          <CheckBox text="Can Modify Settings" name="can_settings" checked={formData.can_settings} onChange={handleChange} />
          <CheckBox text="Can Configure Environment" name="can_configurator" checked={formData.can_configurator} onChange={handleChange} />
          <CheckBox text="Can Manage Users" name="can_user_management" checked={formData.can_user_management} onChange={handleChange} />
          <CheckBox text="Can Manage Vendors" name="can_vendor" checked={formData.can_vendor} onChange={handleChange} />

          
        </div>
        </DialogContent>
        <DialogActions>
              <ButtonOutlined style={{ marginRight: 5 }} onClick={handleCloseDialog}>Cancel</ButtonOutlined>
              {formData.id ? (
                <ButtonContained onClick={handleEditSubmit}>Save</ButtonContained>
              ) : (
                <ButtonContained onClick={handleSubmit}>Create</ButtonContained>
              )}
            </DialogActions>
          
      </Dialog>
    </>
  );
};

export default RolesCard;
